import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import type { Permission } from '@contracts/rbac';
import { AppShell } from '@/components/layout/app-shell';
import { AppShellSkeleton } from '@/components/layout/app-shell-skeleton';
import { TopLoadingBar } from '@/components/layout/TopLoadingBar';
import { lazyPage } from '@/routing/lazy-page';
import { RouteOutlet } from '@/routing/RouteOutlet';
import { useCan, usePrincipal, useSignOut } from './api/query';
import { Notice } from './components/Notice';
import { PageContainer, PageHeader } from './components/PageHeader';
import { NotFoundState } from './components/NotFoundState';
import { NotificationMenu } from './features/activity/NotificationMenu';
import { AssistantProvider } from './features/assistant/AssistantProvider';
import { useOwnMe } from './features/assistant/api';
import { RedirectTo } from './features/assistant/me/RedirectTo';
import { ForcedPasswordPage, InvitePage, LoginPage } from './features/identity/AuthPages';
import { PaletteCardsProvider } from './features/workplace/palette-cards';
import {
  LegacyHistoryRedirect, LegacyProjectRedirect, LegacyTaskRedirect, LegacyTasksRedirect, LegacyWorkplaceRedirect,
} from './features/workplace/redirects';

/*
 * Every private screen is a chunk of its own, fetched when its route first renders (routing/lazy-page.ts).
 * What stays in the entry is what must never wait: the three public pages, and the frame with the inbox. (Work
 * is assigned from the Workplace's own "Việc mới", so the assignment form travels in that page's chunk.) Nothing from this list may be imported statically
 * anywhere in the entry's graph, or its chunk folds back into the entry without a word from the bundler.
 */
const DashboardPage = lazyPage(() => import('./features/dashboard/DashboardPage'), 'DashboardPage');
const WorkplacePage = lazyPage(() => import('./features/workplace/WorkplacePage'), 'WorkplacePage');
const StrategyPage = lazyPage(() => import('./features/strategy/StrategyPage'), 'StrategyPage');
const ObjectivePage = lazyPage(() => import('./features/strategy/ObjectivePage'), 'ObjectivePage');
const OverviewPage = lazyPage(() => import('./features/finance/OverviewPage'), 'OverviewPage');
const FinanceProjectsPage = lazyPage(() => import('./features/finance/ProjectsPage'), 'ProjectsPage');
const FinanceProjectPage = lazyPage(() => import('./features/finance/ProjectPage'), 'ProjectPage');
const EntriesPage = lazyPage(() => import('./features/finance/EntriesPage'), 'EntriesPage');
const SettingsLayout = lazyPage(() => import('./features/finance/SettingsLayout'), 'SettingsLayout');
const BalancePage = lazyPage(() => import('./features/finance/BalancePage'), 'BalancePage');
const ThresholdPage = lazyPage(() => import('./features/finance/ThresholdPage'), 'ThresholdPage');
const RecurringPage = lazyPage(() => import('./features/finance/RecurringPage'), 'RecurringPage');
const CategoriesPage = lazyPage(() => import('./features/finance/CategoriesPage'), 'CategoriesPage');
const AuditPage = lazyPage(() => import('./features/activity/AuditPage'), 'AuditPage');
const HistoryPage = lazyPage(() => import('./features/activity/HistoryPage'), 'HistoryPage');
const PartnersPage = lazyPage(() => import('./features/partners/PartnersPage'), 'PartnersPage');
const PartnerDetailPage = lazyPage(() => import('./features/partners/PartnerDetailPage'), 'PartnerDetailPage');
const ProjectsPage = lazyPage(() => import('./features/projects/ProjectsPage'), 'ProjectsPage');
const ProjectDetailPage = lazyPage(() => import('./features/projects/ProjectDetailPage'), 'ProjectDetailPage');
const AccountPage = lazyPage(() => import('./features/identity/AccountPage'), 'AccountPage');
const AssistantMePage = lazyPage(() => import('./features/assistant/me/AssistantMePage'), 'AssistantMePage');
// D116: the virtual table's library rides in this chunk only.
const BatchPage = lazyPage(() => import('./features/assistant/ingest/BatchPage'), 'BatchPage');
const MissionsPage = lazyPage(() => import('./features/assistant/missions/MissionsPage'), 'MissionsPage');
const AdminUsersPage = lazyPage(() => import('./features/identity/AdminUsersPage'), 'AdminUsersPage');
const RolesPage = lazyPage(() => import('./features/roles/RolesPage'), 'RolesPage');
const AiAdminPage = lazyPage(() => import('./features/assistant-admin/AiAdminPage'), 'AiAdminPage');
const ExtServerDetailPage = lazyPage(() => import('./features/assistant-admin/ExtServerDetailPage'), 'ExtServerDetailPage');
// The OAuth consent and error pages (S2 spec §9.1): outside the shell, like sign-in, but only an AI client sends
// anyone there, so they are chunks of their own rather than weight on every sign-in.
const ConsentPage = lazyPage(() => import('./features/connect/ConsentPage'), 'ConsentPage');
const ConnectErrorPage = lazyPage(() => import('./features/connect/ConnectErrorPage'), 'ConnectErrorPage');
const ActionPage = lazyPage(() => import('./features/connect/ActionPage'), 'ActionPage');
const WelcomePage = lazyPage(() => import('./features/assistant/onboarding/WelcomePage'), 'WelcomePage');

/**
 * Every route the browser can reach. There are exactly two public ones — sign in, and the one-time invitation
 * link — and there is no registration route to reach at all: accounts exist because an administrator
 * invited them. Everything else sits behind the shell, which will not render a private screen until the
 * server has confirmed who is asking.
 */

const CONFIRM_PATH = /^\/connect\/actions\/[0-9a-f-]{36}$/;

/**
 * S8 D130: a person who has neither finished nor deferred the wizard is sent to `/welcome`, from any address but the
 * MCP confirm link (its owner came to answer one action). The exemption is checked before the data, so `/welcome`
 * itself can never loop. It reads the `/me` the assistant provider asks for anyway (no extra request); while the first
 * answer is on its way the shell's silhouette shows, and a failed read lets the person in (fail open). `null` (the
 * server said ASSISTANT_DISABLED, or a test's "unknown") has no onboarding to send anyone to.
 */
function OnboardingGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const own = useOwnMe();
  if (CONFIRM_PATH.test(pathname)) return children;
  if (own.isPending) return <AppShellSkeleton />;
  const me = own.data;
  if (me == null) return children;
  const { completedAt, deferredAt } = me.onboarding;
  if (completedAt === null && deferredAt === null) return <Navigate to="/welcome" replace />;
  return children;
}

/**
 * Every private screen sits behind this: the session, then the forced password change. `bare` is `/welcome`: the
 * same checks and the same assistant provider, but no frame around it (Màn 1 has no rail) and no gate.
 */
function ProtectedShell({ bare = false }: { bare?: boolean }) {
  const location = useLocation();
  const principal = usePrincipal();
  const signOut = useSignOut();
  const navigate = useNavigate();

  if (principal.isPending) {
    // The shell's silhouette; it carries the `page-loading` line the responsive suite waits on.
    return <AppShellSkeleton />;
  }
  // A missing or expired session shows the login screen, never a private screen with empty data in it.
  // The one exception is an action's confirm link (S2 §11-4): an MCP client hands it to its owner, who may well be
  // signed out, so sign-in carries it on as `next` (the login page re-checks the shape, D28).
  if (principal.isError || principal.data == null) {
    const next = CONFIRM_PATH.test(location.pathname) ? `?next=${encodeURIComponent(location.pathname)}` : '';
    return <Navigate to={`/login${next}`} replace />;
  }
  // A session opened with a temporary password may do one thing: replace it. Whatever the address, nothing of the
  // app mounts, so no request is sent that the server would refuse with PASSWORD_CHANGE_REQUIRED.
  if (principal.data.mustChangePassword) return <ForcedPasswordPage />;
  const me = principal.data;

  if (bare) {
    return (
      <>
        <TopLoadingBar />
        <AssistantProvider principal={me}>{() => <RouteOutlet />}</AssistantProvider>
      </>
    );
  }

  return (
    <>
      {/* Outside the frame on purpose: the frame issues no request and so may not read the query cache. */}
      <TopLoadingBar />
      {/* The ⌘K palette's cards, for the same reason (UX wave D, P7). */}
      <PaletteCardsProvider>
        {/* Hana (D109): the provider asks for the prefs, so the frame still issues no request of its own. */}
        <AssistantProvider principal={me}>
          {(assistant) => (
            <OnboardingGate>
              <AppShell
                principal={me}
                notifications={<NotificationMenu />}
                assistant={assistant}
                onSignOut={() => { void signOut().then(() => navigate('/login', { replace: true })); }}
              >
                {/* Inside `<main>`: a screen still loading, or one that failed to, leaves the frame where it is. */}
                <RouteOutlet />
              </AppShell>
            </OnboardingGate>
          )}
        </AssistantProvider>
      </PaletteCardsProvider>
    </>
  );
}

/** An old "Người nhận ngoài công ty" history link: that record is now a partner, and its page is the partner's. */
function ExternalPartyRedirect() {
  const { id = '' } = useParams();
  return <Navigate to={`/partners/${id}`} replace />;
}

/** The section a guarded route belongs to, as its sidebar row names it: the refusal keeps the reader oriented. */
const SECTION_TITLE: Partial<Record<Permission, string>> = {
  'projects.view': 'Dự án',
  'strategy.view': 'Chiến lược',
  'finance.view': 'Tài chính',
  'partners.view': 'Đối tác',
  'audit.view': 'Nhật ký',
  'finance.configure': 'Tài chính',
  'users.view': 'Quản trị tài khoản',
  'roles.view': 'Vai trò & quyền',
  'ai.configure': 'Quản trị AI',
};

/**
 * A page opened by its URL without the permission its sidebar row needs (RBAC spec §5.3): the section's title and
 * the refusal, and the page itself never mounts — so none of its requests is sent. The server refuses them anyway;
 * this only spares the reader a screen of errors.
 */
function RequirePermission({ perm, children }: { perm: Permission; children: ReactNode }) {
  const can = useCan();
  if (can.any(perm)) return children;
  return (
    <PageContainer>
      <PageHeader title={SECTION_TITLE[perm] ?? 'H-Operation'} />
      <Notice tone="error" role="alert">Bạn không có quyền thực hiện thao tác này</Notice>
    </PageContainer>
  );
}

const guard = (perm: Permission, page: ReactNode) => <RequirePermission perm={perm}>{page}</RequirePermission>;

const NotFound = NotFoundState;

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      {/* The consent screen asks the server who is signed in itself, and sends a stranger to sign-in with `next`. */}
      <Route element={<RouteOutlet />}>
        <Route path="/connect/authorize/:id" element={<ConsentPage />} />
        <Route path="/connect/error" element={<ConnectErrorPage />} />
      </Route>
      {/* The first-run wizard (S8 §8.1): signed in, full screen, outside the frame. */}
      <Route element={<ProtectedShell bare />}>
        <Route path="/welcome" element={<WelcomePage />} />
      </Route>
      <Route element={<ProtectedShell />}>
        <Route path="/" element={<DashboardPage />} />
        {/*
          The board lives at `/workplace`. The old addresses under it (a card's uuid from bookmarks and old
          notifications, a former board slug, `?space=`) are redirected by `LegacyWorkplaceRedirect` (sharing spec §5).
        */}
        <Route path="/workplace" element={<LegacyWorkplaceRedirect><WorkplacePage /></LegacyWorkplaceRedirect>} />
        <Route path="/workplace/:legacy/*" element={<LegacyWorkplaceRedirect />} />
        <Route path="/tasks" element={<LegacyTasksRedirect />} />
        <Route path="/tasks/:id" element={<LegacyTaskRedirect />} />
        <Route path="/strategy" element={guard('strategy.view', <StrategyPage />)} />
        <Route path="/strategy/objectives/:id" element={guard('strategy.view', <ObjectivePage />)} />
        <Route path="/finance" element={guard('finance.view', <OverviewPage />)} />
        <Route path="/finance/projects" element={guard('finance.view', <FinanceProjectsPage />)} />
        <Route path="/finance/projects/:id" element={guard('finance.view', <FinanceProjectPage />)} />
        <Route path="/finance/entries" element={guard('finance.view', <EntriesPage />)} />
        {/* Tab "Cài đặt" (finance spec §6): the menu beside the chosen section; the bare address opens the balance. */}
        <Route path="/finance/settings" element={guard('finance.configure', <SettingsLayout />)}>
          <Route index element={<Navigate to="balance" replace />} />
          <Route path="balance" element={<BalancePage />} />
          <Route path="threshold" element={<ThresholdPage />} />
          <Route path="recurring" element={<RecurringPage />} />
          <Route path="categories" element={<CategoriesPage />} />
        </Route>
        {/* Every project the viewer sees; a project's page by its id. An old `/projects/<slug>/…` is redirected. */}
        <Route path="/projects" element={guard('projects.view', <ProjectsPage />)} />
        <Route path="/projects/:projectId/*"
          element={<LegacyProjectRedirect>{guard('projects.view', <ProjectDetailPage />)}</LegacyProjectRedirect>} />
        <Route path="/partners" element={guard('partners.view', <PartnersPage />)} />
        <Route path="/partners/:id" element={guard('partners.view', <PartnerDetailPage />)} />
        {/* The old "Người nhận ngoài công ty" pages became the partner directory (projects & stakeholders spec §3.3). */}
        <Route path="/recipients" element={<Navigate to="/partners" replace />} />
        <Route path="/activity" element={guard('audit.view', <AuditPage />)} />
        {/* Every history link the product hands out lands here; none of them may dangle. Not guarded here: which
            permission a history needs depends on its kind, and the server answers that one (RBAC Task 9). */}
        <Route path="/history/task/:id" element={<LegacyHistoryRedirect />} />
        <Route path="/history/external-party/:id" element={<ExternalPartyRedirect />} />
        <Route path="/history/:kind/:id" element={<HistoryPage />} />
        <Route path="/account" element={<AccountPage />} />
        {/* "Trợ lý của tôi" (S8 §9): every signed-in person's own; the server answers ASSISTANT_DISABLED when it is off.
            The old addresses (bookmarks, stored OAuth return_to rows, old notices) land on their tab, query and hash kept. */}
        <Route path="/assistant/me" element={<AssistantMePage />} />
        <Route path="/settings/assistant" element={<RedirectTo tab="nhip" />} />
        <Route path="/settings/assistant/memory" element={<RedirectTo tab="tri-nho" />} />
        <Route path="/assistant/batches/:id" element={<BatchPage />} />
        <Route path="/assistant/missions" element={<MissionsPage />} />
        {/* The owner's confirm page for an MCP action (S2 §9.3): in the shell, and answered by the page itself. */}
        <Route path="/connect/actions/:id" element={<ActionPage />} />
        <Route path="/admin/users" element={guard('users.view', <AdminUsersPage />)} />
        <Route path="/admin/roles" element={guard('roles.view', <RolesPage />)} />
        <Route path="/admin/ai" element={guard('ai.configure', <AiAdminPage />)} />
        <Route path="/admin/ai/ext-servers/:id" element={guard('ai.configure', <ExtServerDetailPage />)} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
